import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeN1, computeHorizonSnapshots } from '../../utils/estimation';
import { useEstimationStore } from '../../stores/estimationStore';
import { fmt, fmtCHF } from '../../utils/format';
import type { GrandProjet } from '../../types/estimation';
import styles from './HorizonDashboard.module.css';

interface Props {
  project: GrandProjet;
}

export function HorizonDashboard({ project }: Props) {
  const { t } = useTranslation(['finances', 'common']);
  const { tenantBenchmarks } = useEstimationStore();
  const haTotal = project.ha_total ?? 0;
  const haZi = project.ha_industriel ?? 0;
  const defaultHorizon = project.horizon_years ?? 30;

  const [horizonYears, setHorizonYears] = useState(defaultHorizon);

  const estimation = useMemo(() => {
    if (haTotal <= 0) return null;
    return computeN1(haTotal, haZi, {}, tenantBenchmarks);
  }, [haTotal, haZi, tenantBenchmarks]);

  const snapshots = useMemo(() => {
    if (!estimation) return [];
    return computeHorizonSnapshots(estimation, horizonYears);
  }, [estimation, horizonYears]);

  if (haTotal <= 0) {
    return (
      <div className={styles.dashboard}>
        <div style={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', padding: 20 }}>
          Pas de donn&eacute;es de surface.
          &Eacute;ditez le projet pour ajouter la surface totale.
        </div>
      </div>
    );
  }

  if (!estimation) return null;

  const haMixte = Math.max(0, haTotal - haZi);

  return (
    <div className={styles.dashboard}>
      <div className={styles.title}>
        <span>Analyse multi-horizon</span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 400 }}>
          {fmt(haTotal)} ha total
          {haZi > 0 && ` (${fmt(haMixte)} ha mixte + ${fmt(haZi)} ha ZI)`}
        </span>
      </div>

      {/* KPI summary row */}
      <div className={styles.kpiRow}>
        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>{t('logementsLbl', 'Logements')}</div>
          <div className={styles.kpiValue}>{fmt(estimation.logements)}</div>
        </div>
        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>{t('habitantsLbl', 'Habitants')}</div>
          <div className={styles.kpiValue}>{fmt(estimation.inhabitants)}</div>
        </div>
        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>{t('emploisLbl', 'Emplois')}</div>
          <div className={styles.kpiValue}>{fmt(estimation.emplois)}</div>
        </div>
        <div className={styles.kpiItem}>
          <div className={styles.kpiLabel}>{t('coutTotal', 'Coût total')}</div>
          <div className={styles.kpiValue}>{fmtCHF(estimation.cost_total)}</div>
        </div>
      </div>

      {/* Horizon slider */}
      <div className={styles.sliderRow}>
        <span className={styles.sliderLabel}>{t('horizonProjection', 'Horizon')}:</span>
        <input
          className={styles.slider}
          type="range"
          min={5}
          max={50}
          step={1}
          value={horizonYears}
          onChange={(e) => setHorizonYears(parseInt(e.target.value))}
        />
        <span className={styles.sliderValue}>{horizonYears} {t('common:years', 'ans')}</span>
      </div>

      {/* Horizon projection table */}
      {snapshots.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('year', 'Année')}</th>
              <th>{t('habitantsLbl', 'Habitants')}</th>
              <th>{t('logementsLbl', 'Logements')}</th>
              <th>{t('emploisLbl', 'Emplois')}</th>
              <th>{t('cumulativeCost', 'Coûts cumulés')}</th>
              <th>{t('cumulativeSubsidies', 'Revenus cumulés')}</th>
              <th>{t('amortAnnuel', 'Amort. annuel')}</th>
              <th>{t('netCumul', 'Solde cumulé')}</th>
            </tr>
          </thead>
          <tbody>
            {snapshots.map((s) => (
              <tr key={s.year}>
                <td>An {s.year}</td>
                <td>{fmt(s.inhabitants)}</td>
                <td>{fmt(s.logements_delivered)}</td>
                <td>{fmt(s.emplois_delivered)}</td>
                <td>{fmtCHF(s.cumulative_cost)}</td>
                <td>{fmtCHF(s.cumulative_revenue)}</td>
                <td>{fmtCHF(s.annual_amort)}</td>
                <td className={s.cumulative_net >= 0 ? styles.positive : styles.negative}>
                  {fmtCHF(s.cumulative_net)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Zone breakdown */}
      {haZi > 0 && estimation.zi && (
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          <div style={{ padding: 10, background: 'rgba(255,255,255,0.02)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>{t('common:programmeMixte', 'Programme mixte')} ({fmt(haMixte)} ha)</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              {fmt(estimation.logements)} log. &middot; {fmt(estimation.inhabitants)} hab. &middot; {fmtCHF(estimation.cost_total_mix)}
            </div>
          </div>
          <div style={{ padding: 10, background: 'rgba(255,255,255,0.02)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>{t('common:zoneIndustrielle', 'Zone industrielle')} ({fmt(haZi)} ha)</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              {fmt(estimation.zi.emplois)} emplois &middot; {fmtCHF(estimation.zi.cost_total)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
